'use client';

import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';
import TextField from '../inputs/TextField';
import { Form, FormField, FormItem, FormControl } from '../Form';
import styles from './inlineForm.module.scss';
import Button from '../Button';
import { cva, VariantProps } from 'class-variance-authority';

export const InlineFormClass = cva(styles.inlineForm, {
  variants: {
    colorScheme: {
      primary: styles.primary,
      secondary: styles.secondary
    }
  },
  defaultVariants: {
    colorScheme: 'primary'
  }
});

export type InlineFormProps = {
  onSubmit: (value: string) => void | Promise<void>;
  buttonLabel?: React.ReactNode;
  placeholder?: string;
  inputType?: string;
  className?: string;
  buttonProps?: React.ComponentProps<typeof Button>;
  inputProps?: React.ComponentProps<typeof TextField>;
} & VariantProps<typeof InlineFormClass>;

const InlineFormSchema = z.object({
  email: z.string().email()
});

type InlineFormValues = z.infer<typeof InlineFormSchema>;

export default function InlineForm({
  onSubmit,
  buttonLabel = 'Submit',
  placeholder = 'Email',
  inputType = 'email',
  className,
  buttonProps,
  inputProps,
  colorScheme = 'primary'
}: Readonly<InlineFormProps>) {
  const form = useForm<InlineFormValues>({
    resolver: zodResolver(InlineFormSchema),
    defaultValues: { email: '' }
  });

  const handleSubmit = (values: InlineFormValues) => onSubmit(values.email);

  return (
    <div className={InlineFormClass({ className, colorScheme })}>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className={styles.form}
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => {
              return (
                <FormItem className={styles.formItem}>
                  <div className={`${styles.inputWrapper}`}>
                    <FormControl>
                      <TextField
                        placeholder={placeholder}
                        aria-label={'text input'}
                        type={inputType}
                        {...field}
                        className={styles.input}
                        {...inputProps}
                      />
                    </FormControl>
                    <Button
                      type="submit"
                      size="small"
                      {...buttonProps}
                      className={`${styles.button} ${
                        buttonProps?.className || ''
                      }`}
                    >
                      {buttonLabel}
                    </Button>
                  </div>
                </FormItem>
              );
            }}
          />
        </form>
      </Form>
    </div>
  );
}
